import React from "react";
import { FanOrderRow, pillFor, PillKind } from "../lib/fanRequestsModel";
import { formatSingleAmount } from "../utils/currency";
import { calculateFeeBreakdown } from "../utils/feeCalculator";
import { t } from "../i18n";

// 🎟️ The one-word status pill on a My Requests row (founder round-2 pick A,
// locked): brand glyphs (11px inline SVG, no system emoji — the standing
// rule), "Not charged" for a released hold, Waiting → Seen the moment the
// DJ opens their list. Widths were measured in the round-2 gallery: every
// pill here stays under ~90px next to a long title at 375pt; the only wide
// one is the live counter, which carries the DJ's new price on purpose.
//
// ⭐ on Boosted is a founder-blessed exception (points card family).

const G: React.FC<{ d: string; fill?: boolean }> = ({ d, fill }) => (
  <svg
    className="fps-g"
    viewBox="0 0 24 24"
    fill={fill ? "currentColor" : "none"}
    stroke={fill ? "none" : "currentColor"}
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d={d} />
  </svg>
);

const CLOCK = "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zm0 4v5l3 2";
const EYE = "M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12zm10-3a3 3 0 1 0 0 6 3 3 0 0 0 0-6z";
const CHECK = "M5 12l5 5L19 7";
const CIRCLE_CHECK =
  "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm-1.5 14.2L6.3 12l1.6-1.6 2.6 2.6 5.6-5.6 1.6 1.6-7.2 7.2z";
const BOLT = "M13.2 2 5 13.4h5l-1.6 8.6L17 10.4h-5.2Z";
const ROTATE = "M3 12a9 9 0 1 0 9-9M3 4v5h5";

export const PILL_LABEL: Record<PillKind, string> = {
  waiting: t("fanStatusPill.waiting"),
  seen: t("fanStatusPill.seen"),
  boosted: t("fanStatusPill.boosted"),
  counter: t("fanStatusPill.newPrice"),
  accepted: t("fanStatusPill.accepted"),
  played: t("fanStatusPill.played"),
  checking: t("fanStatusPill.checking"),
  declined: t("fanStatusPill.declined"),
  notcharged: t("fanStatusPill.notCharged"),
  noreply: t("fanStatusPill.noReply"),
  refunded: t("fanStatusPill.refunded"),
  failed: t("fanStatusPill.paymentFailed"),
};

const FanStatusPill: React.FC<{ row: FanOrderRow; className?: string }> = ({ row, className }) => {
  const kind = pillFor(row);
  const cls = `fps-pill fps-pill--${kind}${className ? ` ${className}` : ""}`;
  switch (kind) {
    case "waiting":
      return (
        <span className={cls}>
          <G d={CLOCK} />
          {PILL_LABEL.waiting}
        </span>
      );
    case "checking":
      // "Wasn't played?" claim waiting on the talent's set list — the Waiting
      // look (founder, 2026-10-09).
      return (
        <span className={`${cls} fps-pill--waiting`}>
          <G d={CLOCK} />
          {PILL_LABEL.checking}
        </span>
      );
    case "seen":
      return (
        <span className={cls}>
          <G d={EYE} />
          {PILL_LABEL.seen}
        </span>
      );
    case "boosted":
      return <span className={cls}>⭐ {PILL_LABEL.boosted}</span>;
    case "counter":
      // The all-in total — the same number the live card's "New price", the
      // counter email and the Apple Pay sheet show. Never the bare bid.
      return (
        <span className={cls}>
          <G d={BOLT} fill />
          {formatSingleAmount(
            calculateFeeBreakdown(row.counterAmount as number, row.currency).customerTotal,
            row.currency
          )}
        </span>
      );
    case "accepted":
      return (
        <span className={cls}>
          <G d={CHECK} />
          {PILL_LABEL.accepted}
        </span>
      );
    case "played":
      return (
        <span className={cls}>
          <G d={CIRCLE_CHECK} fill />
          {PILL_LABEL.played}
        </span>
      );
    case "refunded":
      return (
        <span className={cls}>
          <G d={ROTATE} />
          {PILL_LABEL.refunded}
        </span>
      );
    default:
      return <span className={cls}>{PILL_LABEL[kind]}</span>;
  }
};

export default FanStatusPill;
